import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { ImportBucket } from '../model/imports.state.types'
import { Source } from '@/app/constants/import/source'
import { FaIcon, faPeopleArrows, faPeopleAssigned } from '@/lib/fa'
import {
  getSourceIcon,
  getStatusLabel,
  getStatusPillClasses,
  getStatusUserIcon,
} from '../pages/home/components/import-review/utils'
import { getSourceName } from '@/app/common/converters/import/sourceToTextConverter'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import clx from 'classnames'
import { Checkbox } from '@/ui'
import styles from './styles.module.css'

import { DateField } from '@/ui/form/controls/DateField'

type DriverRowProps = {
  checked?: boolean
  onCheck?: (checked: boolean) => void

  name: string
  id: string
  status: ImportBucket
  days: number
  sources: Source[]
  className?: string
  children?: ReactNode

  assigned?: boolean
  handleAssign?: () => void

  // vehicles: (DriverVehicleImportItem & { sources: Source[] })[]

  /** Optional - if provided, will be used as initial value and synced */
  expanded?: boolean
  /** Optional - called when user toggles expand */
  onToggleExpand?: (expanded: boolean) => void

  dateFrom?: string
  handleDateFrom?: (date: string) => void
}

export const DriverRow: React.FC<DriverRowProps> = ({
  checked = false,
  onCheck,
  name,
  id,
  status,
  days,
  sources,
  expanded = false,
  onToggleExpand,
  className,
  children,
  handleAssign,
  assigned,
  dateFrom,
  handleDateFrom,
}) => {
  const { t } = useLocalize()
  const allowChange = status !== 'existing'

  // Internal expanded state
  const [isExpanded, setIsExpanded] = useState<boolean>(expanded)

  // Sync if parent changes expanded prop
  useEffect(() => {
    setIsExpanded(expanded)
  }, [expanded])

  const hasChildren = useMemo(() => Boolean(children), [children])

  // Auto-expand when dateFrom is set so the user sees filtered vehicles
  useEffect(() => {
    if (dateFrom && !isExpanded && hasChildren) {
      setIsExpanded(true)
    }
  }, [dateFrom]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleExpand = () => {
    if (!hasChildren) return

    setIsExpanded((prev) => {
      const next = !prev
      onToggleExpand?.(next)
      return next
    })
  }

  return (
    <div>
      <div
        className={clx(
          'tw-grid tw-items-center tw-px-4 tw-py-2.5 tw-text-sm hover:tw-bg-slate-50',
          className,
        )}
      >
        {/* Checkbox */}
        <div className="tw-flex tw-items-center tw-justify-center">
          <Checkbox
            className="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300 tw-text-blue-600 focus:tw-ring-blue-500"
            checked={checked}
            onChange={(e) => onCheck?.(e.target.checked)}
            disabled={!allowChange}
            readOnly={!allowChange}
          />
        </div>

        {/* Name */}
        <div className={clx('tw-flex tw-items-center tw-gap-2', styles.titleCol)}>
          <div className="tw-flex tw-flex-col tw-flex-wrap tw-justify-start tw-gap-1 lg:tw-flex-row lg:tw-items-center">
            <span className="tw-flex tw-items-center tw-gap-1 tw-font-medium tw-text-slate-900">
              <FaIcon icon={getStatusUserIcon(status)} /> {name}
            </span>
            {id ? (
              <span className="tw-text-xs tw-text-slate-500">{`ID: ${id}`}</span>
            ) : null}
          </div>
        </div>

        {/* Status */}
        <div>
          <span
            className={`tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium ${getStatusPillClasses(
              status,
            )}`}
          >
            {getStatusLabel(status, t)}
          </span>
        </div>

        {/* Source */}
        <div className="tw-flex tw-flex-col tw-gap-1 tw-text-sm tw-text-slate-700">
          {sources.map((s, i) => (
            <span key={i}>
              <FaIcon icon={getSourceIcon(s)} /> {getSourceName(s, t)}
            </span>
          ))}
        </div>

        {/* Relations */}
        <div className="tw-flex tw-text-blue-600 tw-cursor-pointer" onClick={handleAssign}>
          <FaIcon icon={assigned ? faPeopleAssigned : faPeopleArrows} />
        </div>

        {/* Days */}
        <div className="tw-text-sm">{days}</div>

        {/* Range */}
        <div>
          {/* <button
            type="button"
            className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-gray-200 tw-px-3 tw-py-1.5 tw-text-sm hover:tw-bg-gray-100"
          >
            Wczytaj od
            <span className="tw-text-gray-400">Ustaw…</span>
          </button> */}
          <DateField 
            openDirection='down'
            setValue={handleDateFrom}
            value={dateFrom || ""}
            // label="Wczytaj od"
            placeholder="Wczytaj od..."
            // helper?: React.ReactNode
            // error?: string | null
          />
        </div>

        {/* Expand */}
        <button
          type="button"
          onClick={handleToggleExpand}
          disabled={!hasChildren}
          aria-expanded={isExpanded}
          className={clx(
            'tw-ml-auto tw-rounded tw-p-2 tw-text-gray-500 hover:tw-text-gray-700',
            !hasChildren && 'tw-cursor-not-allowed tw-opacity-40 hover:tw-text-gray-500',
          )}
        >
          <FaIcon icon={isExpanded ? faChevronUp : faChevronDown} />
        </button>
      </div>

      {/* Children table */}
      {hasChildren && isExpanded && (
        <div id="driver-children">
          {children}
        </div>
      )}
    </div>
  )
}
